<script lang="ts">
  import { Fieldset, Icon } from '@factry/components'
  import Graph from '../components/Graph.svelte'
  import { co2UOM, lengthUOM, roundNumber } from '../utils/datasets'
  import type { AssetEvent } from '../types'

  export let event: AssetEvent | undefined
  export let length: number

  let total = '0'
  let totalRecycled = '0'

  function setTotals (): void {
    if (event?.gas_co2_emission_total && event?.conveyer_belt_co2_emission_total && event?.extruder_1_co2_emission_total) {
      total = roundNumber(Number(event.gas_co2_emission_total) + Number(event.conveyer_belt_co2_emission_total) + Number(event.extruder_1_co2_emission_total))
    }

    if (event?.gas_co2_recycling && event?.conveyer_belt_co2_recycling && event?.extruder_1_co2_recycling) {
      totalRecycled = roundNumber(Number(event.gas_co2_recycling) + Number(event.conveyer_belt_co2_recycling) + Number(event.extruder_1_co2_recycling))
    }
  }

  $: if (event) setTotals()
</script>

<style lang="scss">
  @import '../utils/base';

  .row {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing * 0.5;
    justify-content: center;

    &:not(:last-child) {
      margin-bottom: $spacing * 0.5;
    }

    :global(.fieldset) {
      background-color: $white;
      border-radius: $radius !important;
      border: 1px solid $border;
      padding: $spacing !important;
      width: 100%;

      :global(legend) {
        text-shadow: 2px 2px 2px white,
          -2px 2px 2px white,
          2px -2px 2px white,
          -2px -2px 2px white !important;
      }
    }
  }

  .group {
    align-items: center;
    background-color: $white;
    border-radius: $radius;
    border: 1px solid $border;
    flex: 1;
    max-width: 340px; // Almost aligns with the tabs
    min-width: 250px;
    padding: $spacing;

    @media (max-width: 600px) {
      max-width: none;
    }

    &.is-wide {
      max-width: none;
      min-width: min(500px, 100%);

      &.is-flex {
        flex-wrap: wrap;
        justify-content: space-between;
      }
    }

    &.no-styling {
      border: 0;
      padding: 0 !important;
    }

    &.is-colored {
      background-color: $primary-light;
      border-color: $primary;
      padding-left: $spacing * 1.75 !important;
      position: relative;

      &::before {
        -webkit-font-smoothing: antialiased;
        color: $primary-dark;
        content: '\e097';
        display: inline-block;
        font: var(--fa-font-solid);
        left: $spacing * 0.5;
        position: absolute;
        text-rendering: auto;
        top: 50%;
        transform: translateY(-50%);
      }

      .title {
        color: $primary-dark;
      }
    }

    :global(.fieldset) &,
    .group {
      padding: $spacing * 0.5;
    }
  }

  .icon-wrapper {
    background-color: $grey-light;
    border-radius: $radius;
    color: $grey-dark;
    float: left;
    margin-right: $spacing * 0.5;
    padding: $spacing * 0.33;
  }

  .title {
    margin-bottom: $spacing * 0.25;
  }

  .description {
    color: $grey;
    font-size: $size-5;

    b {
      color: $grey-darker;
    }

    + .row {
      margin-top: $spacing * 0.5;
    }
  }

  .value {
    color: $grey-darker;
    font-size: $size-medium;
    font-weight: $weight-medium;
  }

  .graph {
    aspect-ratio: 3 / 1;
    margin-top: $spacing * 0.5;
    max-height: 300px;
    width: 100%;
  }
</style>

<div class="row">
  <div class="group is-flex">
    <div class="icon-wrapper has-background-link-light has-text-link-dark">
      <Icon icon="fa-solid fa-smog" />
    </div>
    <div class="group-inner">
      <div class="description">Gas CO₂ emission</div>
      <div class="value">{roundNumber(event?.gas_co2_emission_total || 0)} {co2UOM}</div>
    </div>
  </div>
  <div class="group is-flex">
    <div class="icon-wrapper has-background-primary-light has-text-primary-dark">
      <Icon icon="fa-solid fa-angles-right" />
    </div>
    <div class="group-inner">
      <div class="description">Conveyer belt CO₂ emission</div>
      <div class="value">{roundNumber(event?.conveyer_belt_co2_emission_total || 0)} {co2UOM}</div>
    </div>
  </div>
  <div class="group is-flex">
    <div class="icon-wrapper has-background-info-light has-text-info-dark">
      <Icon icon="fa-solid fa-filter" />
    </div>
    <div class="group-inner">
      <div class="description">Extruder CO₂ emission</div>
      <div class="value">{roundNumber(event?.extruder_1_co2_emission_total || 0)} {co2UOM}</div>
    </div>
  </div>
</div>

<div class="row">
  <div class="group is-wide">
    <h3 class="title is-4">Lifecycle CO₂ breakdown</h3>
    <div class="description">
      Tracking the total lifecycle CO₂ emitted
    </div>
    <div class="row">
      <div class="group is-wide is-colored">
        <h3 class="title is-4">Total CO₂: {total} {co2UOM} CO₂ emitted</h3>
        <div class="description">
          Target limit: &lt; {Math.ceil(Number(total) / 0.8)} {co2UOM} • 80% of limit
        </div>
      </div>
    </div>
    <div class="row">
      {#if event}
        <section class="graph">
          <Graph
            {event}
            partitionKey="co2_emission_total"
            type="doughnut"
          />
        </section>
      {/if}
    </div>
  </div>
  <div class="group is-wide">
    <h3 class="title is-4">Recycling CO₂ breakdown</h3>
    <div class="description">
      Tracking the total recycling CO₂ done
    </div>
    <div class="row">
      <div class="group is-wide is-colored">
        <h3 class="title is-4">Total CO₂: {totalRecycled} {co2UOM} CO₂ recycled</h3>
        <div class="description">
          Target limit: &lt; {Math.ceil(Number(totalRecycled) / 0.8)} {co2UOM} • 80% of limit
        </div>
      </div>
    </div>
    <div class="row">
      {#if event}
        <section class="graph">
          <Graph
            {event}
            partitionKey="co2_recycling"
            type="doughnut"
          />
        </section>
      {/if}
    </div>
  </div>
</div>

<div class="row">
  <Fieldset label="Environmental Impact" hasFloatingLegend>
    <div class="row">
      <div class="group is-wide no-styling">
        <h3 class="title is-4">Energy consumption</h3>
      </div>
    </div>
    <div class="row">
      <div class="group is-wide is-flex">
        <div class="group-inner">
          <h3 class="title is-4">Total energy consumed</h3>
          <div class="description">
            This is the total amount of electrical and thermal energy used to manufacture one {length} {lengthUOM} section of pipe.
          </div>
        </div>
        <div class="value">{total} {co2UOM} per {length} {lengthUOM} joint</div>
      </div>
    </div>
    <div class="row">
      <div class="group is-wide is-flex">
        <div class="group-inner">
          <h3 class="title is-4">Renewable energy used</h3>
          <div class="description">
            Portion of total energy that came from renewable sources (solar, hydro, wind) through either direct generation or power-purchase agreements.
          </div>
        </div>
        <div class="value">{totalRecycled} {co2UOM} per {length} {lengthUOM} joint (about {Math.round(Number(totalRecycled) / Number(total) * 100)} % of total energy)</div>
      </div>
    </div>
    <div class="row">
      <div class="group is-wide is-flex">
        <div class="group-inner">
          <h3 class="title is-4">Transport energy consumption</h3>
          <div class="description">
            Energy required to move the finished pipe from the manufacturing facility to the installation site.
          </div>
        </div>
        <div class="value">{roundNumber(event?.gas_co2_emission_total || '0')} {co2UOM} per {length} {lengthUOM} joint</div>
      </div>
    </div>
  </Fieldset>
</div>
